import { bitcoinjs } from '@optimex-xyz/btc-script'
import { broadcastTx } from '@optimex-xyz/trade-wallet-sdk'

import { INetwork } from '@/types'
import { isBtcChain, isMobileDevice, SUPPORTED_NETWORK, WALLET_IM_TOKEN } from '@/utils'
import { CustomError } from '@/utils/error'
import { sendLog } from '@/utils/logger'
import { generatePsbtSendBtc } from '@/utils/transaction/btc'

import { BaseWallet } from './BaseWallet'
import { IWalletConnectOptions, PsbtSignOptions, TransactionOptions } from './types'
import { ImTokenBitcoinProvider } from './types/imToken.type'

interface WalletState {
  connectedAddresses: Record<string, string>
  compressedPublicKeys: Record<string, string>
  currentNetwork: string | null
  isDisconnecting: boolean
}

export class ImTokenWallet extends BaseWallet {
  public readonly name = 'ImToken Wallet'
  public readonly key = WALLET_IM_TOKEN
  private readonly downloadUrl = 'https://token.im/download'

  private readonly state: WalletState = {
    connectedAddresses: {},
    compressedPublicKeys: {},
    currentNetwork: null,
    isDisconnecting: false,
  }

  private readonly eventListeners = {
    accountsChanged: new Map<string, ((...args: unknown[]) => void) | null>(),
    disconnect: new Map<string, ((...args: unknown[]) => void) | null>(),
  }

  private get provider(): ImTokenBitcoinProvider | undefined {
    return window.bitcoin
  }

  private get isProviderAvailable(): boolean {
    return typeof window !== 'undefined' && 'bitcoin' in window && window.bitcoin !== undefined
  }

  private validateProvider(): void {
    if (!this.isInstalled()) {
      this.throwDownloadError()
    }

    if (!this.provider) {
      throw new CustomError('Provider is not available')
    }
  }

  private validateBtcNetwork(networkId?: string): void {
    if (networkId && !isBtcChain(networkId)) {
      throw new Error(`Unsupported network: ${networkId}`)
    }
  }

  private handleMobileRedirect(): void {
    if (isMobileDevice() && !this.provider) {
      this.redirectToImTokenWallet()
      throw new CustomError('Redirecting to imToken Wallet...')
    }
  }

  private redirectToImTokenWallet(): void {
    window.location.href = this.downloadUrl
  }

  private throwDownloadError(): void {
    throw new CustomError(
      `imToken Wallet is not installed! <a target="_blank" class="underline" href="${this.downloadUrl}">Download wallet</a>`
    )
  }

  private async connectToNetwork(networkId: string): Promise<string[]> {
    const network = this.mapNetworkIdToImTokenNetwork(networkId)

    try {
      await this.provider!.request({ method: 'btc_switchNetwork', params: [network] })
    } catch (error) {
      console.error(`ImTokenWallet connect ${network} error:`, error)
      // Network switching might not be supported yet, continue with current network
    }
    return await this.requestAccounts()
  }

  private async requestAccounts(): Promise<string[]> {
    return await this.provider!.request({ method: 'btc_requestAccounts' })
  }

  private async getPublicKey(): Promise<string> {
    return await this.provider!.request({ method: 'btc_getPublicKey' })
  }

  private mapNetworkIdToImTokenNetwork(networkId: string): string {
    return networkId === SUPPORTED_NETWORK.BTC ? 'mainnet' : 'signet'
  }

  private setupEventListeners(networkId: string, options: IWalletConnectOptions): void {
    if (!this.provider || typeof this.provider.on !== 'function') return

    this.removeEventListeners(networkId)

    this.eventListeners.accountsChanged.set(networkId, (...args: unknown[]) => {
      const accounts = args[0] as string[]
      if (accounts?.length) {
        options.onAccountChanged?.({ address: accounts[0], networkInfo: options.network })
      }
    })

    this.eventListeners.disconnect.set(networkId, () => options.onDisconnected?.(this.key))

    this.provider.on('accountsChanged', this.eventListeners.accountsChanged.get(networkId)!)
    this.provider.on('networkChanged', this.eventListeners.disconnect.get(networkId)!)
  }

  private removeEventListeners(networkId: string): void {
    if (!this.provider) return

    const accountsChangedListener = this.eventListeners.accountsChanged.get(networkId)
    const disconnectListener = this.eventListeners.disconnect.get(networkId)

    if (accountsChangedListener) {
      this.provider.removeListener('accountsChanged', accountsChangedListener)
      this.eventListeners.accountsChanged.set(networkId, null)
    }

    if (disconnectListener) {
      this.provider.removeListener('networkChanged', disconnectListener)
      this.eventListeners.disconnect.set(networkId, null)
    }
  }

  private async disconnectFromNetwork(networkId: string): Promise<void> {
    if (!isBtcChain(networkId)) return

    this.removeEventListeners(networkId)
  }

  private cleanupState(networkId?: string): void {
    this.state.currentNetwork = null

    if (networkId) {
      delete this.state.connectedAddresses[networkId]
      delete this.state.compressedPublicKeys[networkId]
    }
  }

  public isInstalled(): boolean {
    if (isMobileDevice()) {
      return true
    }
    return this.isProviderAvailable
  }

  public isDetected(): boolean {
    return !!this.provider
  }

  public priority(): number {
    return this.provider || this.isInstalled() ? 0 : -1
  }

  public async getAddress(networkId: string): Promise<string> {
    this.validateProvider()
    this.validateBtcNetwork(networkId)

    if (this.state.connectedAddresses[networkId]) {
      return this.state.connectedAddresses[networkId]
    }

    const accounts = await this.requestAccounts()
    const address = accounts[0]

    this.state.connectedAddresses[networkId] = address
    this.state.compressedPublicKeys[networkId] = await this.getPublicKey()

    return address
  }

  public async connect(
    networkId: string,
    options: IWalletConnectOptions
  ): Promise<{ address: string; networkInfo: INetwork; compressedPublicKey: string | undefined }> {
    this.handleMobileRedirect()
    this.validateProvider()
    this.validateBtcNetwork(networkId)

    this.state.currentNetwork = networkId
    this.setupEventListeners(networkId, options)
    const accounts = await this.connectToNetwork(networkId)
    const address = accounts[0]

    this.state.connectedAddresses[networkId] = address
    this.state.compressedPublicKeys[networkId] = await this.getPublicKey()

    return {
      address,
      networkInfo: options.network,
      compressedPublicKey: this.state.compressedPublicKeys[networkId],
    }
  }

  public async disconnect(networkId?: string): Promise<void> {
    if (this.state.isDisconnecting) return
    this.state.isDisconnecting = true
    this.validateProvider()

    try {
      const targetNetwork = networkId || this.state.currentNetwork
      if (targetNetwork) {
        await this.disconnectFromNetwork(targetNetwork)
      }

      this.cleanupState(networkId)
    } finally {
      this.state.isDisconnecting = false
    }
  }
  private finalizeAllInputs(signedPsbt: bitcoinjs.Psbt, selectedUtxos: any[]): void {
    // Finalize all inputs manually
    for (let i = 0; i < selectedUtxos.length; i++) {
      try {
        signedPsbt.finalizeInput(i)
      } catch (finalizeError) {
        console.warn(`Failed to finalize input ${i}, attempting alternative finalization:`, finalizeError)
        // For all input types, try finalization with empty witness
        signedPsbt.finalizeInput(i, () => ({
          finalScriptSig: undefined,
          finalScriptWitness: undefined,
        }))
      }
    }
  }
  public async sendTransaction(
    toAddress: string,
    amount: number | bigint,
    networkId: string,
    options?: TransactionOptions
  ): Promise<string> {
    this.validateProvider()
    this.validateBtcNetwork(networkId)

    try {
      sendLog({
        message: 'imtoken start send',
        data: { toAddress, amount, network: networkId, options },
        tags: this.key,
      })
      const fromAddress = this.state.connectedAddresses[networkId]
      if (!fromAddress) {
        throw new CustomError('Bitcoin wallet not connected')
      }

      const publicKey = this.state.compressedPublicKeys[networkId]
      if (!publicKey) {
        throw new CustomError('Public key not available')
      }

      // Generate PSBT using the utility function
      const { psbt, btcNetwork, selectedUtxos } = await generatePsbtSendBtc({
        toAddress,
        _amount: amount,
        fromAddress,
        networkId,
        publicKey,
        options,
      })
      const psbtHex = psbt.toHex()
      const signedPsbtHex = await this.provider!.request({
        method: 'btc_signPsbt',
        params: [psbtHex, false], // autoFinalize = false to handle complex inputs properly
      })
      const signedPsbt = bitcoinjs.Psbt.fromHex(signedPsbtHex, { network: btcNetwork })
      this.finalizeAllInputs(signedPsbt, selectedUtxos)
      // Extract transaction and broadcast
      const tx = signedPsbt.extractTransaction()
      const txHex = tx.toHex()

      return await broadcastTx(networkId, txHex)
    } catch (error) {
      throw new CustomError(`Transaction failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  // Only bip322-simple is supported.
  public async signMessage(message: string, network?: string): Promise<string> {
    this.validateProvider()
    this.validateBtcNetwork(network)

    return await this.provider!.request({
      method: 'btc_signMessage',
      params: [message, 'bip322-simple'],
    })
  }

  public async signPsbt(psbt: string, network: string, _?: PsbtSignOptions): Promise<string> {
    this.validateBtcNetwork(network)
    this.validateProvider()

    const hexPsbt = bitcoinjs.Psbt.fromBase64(psbt).toHex()

    return await this.provider!.request({
      method: 'btc_signPsbt',
      params: [hexPsbt, true], // autoFinalize = true
    })
  }

  public async getCompressedPublicKey(networkId: string): Promise<string> {
    this.validateProvider()
    this.validateBtcNetwork(networkId)

    if (this.state.compressedPublicKeys[networkId]) {
      return this.state.compressedPublicKeys[networkId]
    }
    const publicKey = await this.getPublicKey()
    this.state.compressedPublicKeys[networkId] = publicKey
    return publicKey
  }
}
